// src/components/PatientMessagesTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  getMessagesForPatient,
  sendMessage,
  deleteMessage,
} from "../APIs/messageService";
import { getProvider } from "../APIs/providerApi";

export default function PatientMessagesTab({ patientId }) {
  const [allMessages, setAllMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [nameCache, setNameCache] = useState({}); // providerId -> "First Last"
  const [activeProviderId, setActiveProviderId] = useState(null); // which thread is open
  const [composer, setComposer] = useState("");
  const [sending, setSending] = useState(false);

  // Load all patient messages
  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const msgs = await getMessagesForPatient(patientId);
        if (!cancelled) setAllMessages(Array.isArray(msgs) ? msgs : []);
      } catch {
        if (!cancelled) setErr("Failed to load messages.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  // Build threads grouped by provider
  const threads = useMemo(() => {
    const byProvider = new Map();
    for (const m of allMessages) {
      const providerId = m.senderProviderId ?? m.recipientProviderId;
      if (!providerId) continue;
      if (!byProvider.has(providerId)) {
        byProvider.set(providerId, { providerId, messages: [] });
      }
      byProvider.get(providerId).messages.push(m);
    }

    for (const t of byProvider.values()) {
      t.messages.sort(
        (a, b) => new Date(a.sentAt || 0) - new Date(b.sentAt || 0),
      );
      t.lastMessage = t.messages[t.messages.length - 1];
    }

    return Array.from(byProvider.values()).sort(
      (a, b) =>
        new Date(b.lastMessage?.sentAt || 0) -
        new Date(a.lastMessage?.sentAt || 0),
    );
  }, [allMessages]);

  // Lookup provider names for threads
  useEffect(() => {
    const missing = threads
      .map((t) => t.providerId)
      .filter((id) => id && !nameCache[id]);
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(
          missing.map(async (id) => {
            try {
              const p = await getProvider(id);
              const full =
                [p.firstName, p.lastName].filter(Boolean).join(" ") ||
                `Provider ${id}`;
              return [id, full];
            } catch {
              return [id, `Provider ${id}`];
            }
          }),
        );
        if (!cancelled) {
          setNameCache((prev) => {
            const next = { ...prev };
            for (const [id, name] of results) next[id] = name;
            return next;
          });
        }
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [threads, nameCache]);

  const activeMessages = useMemo(() => {
    if (!activeProviderId) return [];
    const t = threads.find((x) => x.providerId === activeProviderId);
    return t ? t.messages : [];
  }, [threads, activeProviderId]);

  const openThread = (providerId) => setActiveProviderId(providerId);
  const backToThreads = () => {
    setActiveProviderId(null);
    setComposer("");
  };

  const onSend = async (e) => {
    e.preventDefault();
    if (!composer.trim() || !activeProviderId) return;
    setSending(true);
    setErr("");
    try {
      const created = await sendMessage({
        senderPatientId: patientId,
        recipientProviderId: activeProviderId,
        content: composer.trim(),
      });
      setAllMessages((prev) => [created, ...prev]);
      setComposer("");
    } catch {
      setErr("Failed to send.");
    } finally {
      setSending(false);
    }
  };

  const onDelete = async (messageId) => {
    try {
      await deleteMessage(messageId, { patientId });
      setAllMessages((prev) => prev.filter((m) => m.messageId !== messageId));
    } catch {
      alert("Failed to delete message.");
    }
  };

  const fmtTs = (ts) => (ts ? new Date(ts).toLocaleString() : "");
  const displayName = (pid) => nameCache[pid] || `Provider ${pid}`;

  return (
    <div className="w-full p-4 pl-0">
      {err && <div className="text-red-600">{err}</div>}
      {loading && <div>Loading…</div>}

      {!activeProviderId ? (
        // Thread list
        <div className="space-y-3">
          {threads.length === 0 && !loading ? (
            <div className="text-sm text-gray-600">No messages yet.</div>
          ) : (
            threads.map((t) => (
              <button
                key={t.providerId}
                onClick={() => openThread(t.providerId)}
                className="w-full text-left p-3 border rounded bg-white hover:shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium">{displayName(t.providerId)}</div>
                  <div className="text-xs text-gray-500">
                    {fmtTs(t.lastMessage?.sentAt)}
                  </div>
                </div>
                <div className="text-sm text-gray-700 line-clamp-2 mt-1">
                  {t.lastMessage?.content || "—"}
                </div>
              </button>
            ))
          )}
        </div>
      ) : (
        // Thread detail
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-semibold">
              {displayName(activeProviderId)}
            </h4>
            <button
              onClick={backToThreads}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm"
            >
              ← Back
            </button>
          </div>

          <div className="space-y-3">
            {activeMessages.length === 0 ? (
              <div className="text-sm text-gray-600">
                No messages in this thread yet.
              </div>
            ) : (
              activeMessages.map((m) => {
                const mine = m.senderPatientId === patientId;
                return (
                  <div
                    key={m.messageId}
                    className="p-3 border rounded bg-white"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs text-gray-600">
                          {mine ? "You" : displayName(activeProviderId)} •{" "}
                          {fmtTs(m.sentAt)}
                        </div>
                        <div className="mt-1">{m.content}</div>
                      </div>
                      <button
                        onClick={() => onDelete(m.messageId)}
                        className="text-red-600 text-sm"
                        title="Delete"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <form onSubmit={onSend} className="mt-2">
            <textarea
              className="w-full border p-2 rounded"
              rows={2}
              value={composer}
              onChange={(e) => setComposer(e.target.value)}
              placeholder={`Message ${displayName(activeProviderId)}…`}
              disabled={sending}
            />
            <div className="flex justify-end mt-2">
              <button
                type="submit"
                className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50"
                disabled={sending || !composer.trim()}
              >
                Send
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
